import { useQuery, useMutation } from "@tanstack/react-query";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation, useRoute } from "wouter";
import { StatusBadge } from "@/components/shared/status-badge";
import { WorkRequestDetailContent } from "@/components/admin/intake/work-request-detail-content";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft,
  TicketPlus,
  AlertTriangle,
} from "lucide-react";
import type { WorkRequest } from "@db/schema";
import { getWorkRequestCategoryLabel } from "@shared/work-request";

export default function WorkRequestDetailPage() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [, params] = useRoute("/work-requests/:id");
  const id = params?.id;

  const { data: wr, isLoading } = useQuery<WorkRequest>({
    queryKey: [`/api/intake/work-requests/${id}`],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!id,
  });

  const createTicketMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/intake/work-requests/${id}/create-ticket`,
      );
      return (await res.json()) as {
        success: true;
        ticketId: string;
        ticketKey: string;
      };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: [`/api/intake/work-requests/${id}`],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/intake/work-requests"],
      });
      toast({ title: "Ticket created from work request" });
      navigate(`/tickets/${result.ticketId}`);
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to create ticket",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-6 lg:grid-cols-3">
          <Skeleton className="h-[400px] rounded-lg lg:col-span-2" />
          <Skeleton className="h-[300px] rounded-lg" />
        </div>
      </div>
    );
  }

  if (!wr) {
    return (
      <div className="space-y-6">
        <Button
          variant="ghost"
          onClick={() => navigate("/work-requests")}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Work Requests
        </Button>
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold">Work request not found</h2>
          <p className="text-muted-foreground mt-1">
            The requested work request could not be found.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/work-requests")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge type="workRequest" status={wr.status} />
            {wr.isUrgent && (
              <Badge className="bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20">
                <AlertTriangle className="mr-1 h-3 w-3" />
                Urgent
              </Badge>
            )}
          </div>
          <h1 className="text-xl font-bold font-heading mt-1">
            {wr.fullName} - {getWorkRequestCategoryLabel(wr.category)}
          </h1>
        </div>
      </div>

      <WorkRequestDetailContent
        workRequest={wr}
        onOpenTicket={
          wr.ticketId ? () => navigate(`/tickets/${wr.ticketId}`) : undefined
        }
        statusActions={
          wr.status !== "ticket_created" ? (
            <Button
              className="w-full"
              onClick={() => createTicketMutation.mutate()}
              disabled={createTicketMutation.isPending}
            >
              <TicketPlus className="mr-2 h-4 w-4" />
              {createTicketMutation.isPending ? "Creating..." : "Create Ticket"}
            </Button>
          ) : undefined
        }
      />
    </div>
  );
}
